import {
  OCCASION_CONFIG,
  TEMPLATE_DEFAULT_FONT,
  THEME_PALETTES,
  type ListFont,
  type ListOccasion,
  type ListTheme,
} from "@listou/shared/constants";
import {
  motifTileUrl,
  STICKER_PACKS,
  stickerUrl,
  type MotifLayoutId,
} from "@listou/shared/stickers";
import type { ListAppearance } from "@listou/shared/types/api";
import type { CSSProperties } from "react";

/**
 * A list's palette as CSS variables on its container. Components use the ordinary
 * tokens (`bg-surface`, `text-accent`), so a themed subtree needs no special classes.
 */
export const themeStyle = (theme: ListTheme, accentColor: string | null): CSSProperties => {
  const palette = THEME_PALETTES[theme] ?? THEME_PALETTES.classic;
  const accent = accentColor ?? palette.accent;
  const action = palette.dark ? accent : deepen(accent, palette.ink, "#ffffff", 4.5);
  return {
    "--color-page": palette.background,
    "--color-surface": palette.surface,
    "--color-ink": palette.ink,
    // Palettes are designed for mood; these guarantees are for the grandparents reading
    // them. Secondary text and accent-coloured text are pulled towards the ink until they
    // reach 4.5:1 on the page, and a button until its label does.
    "--color-muted": deepen(palette.muted, palette.ink, palette.background, 4.6),
    "--color-line": `color-mix(in srgb, ${palette.ink} 14%, transparent)`,
    "--color-accent": accent,
    "--color-accent-ink": readableOn(accent),
    "--color-accent-text": deepen(accent, palette.ink, palette.background, 4.5),
    "--color-action": action,
    "--color-action-ink": palette.dark ? readableOn(action) : "#ffffff",
    colorScheme: palette.dark ? "dark" : "light",
  } as CSSProperties;
};

const channels = (hex: string) => {
  const value = hex.replace("#", "");
  return [0, 2, 4].map((i) => Number.parseInt(value.slice(i, i + 2), 16));
};

const toHex = (rgb: number[]) =>
  `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;

const mix = (from: string, to: string, amount: number) => {
  const a = channels(from);
  const b = channels(to);
  return toHex(a.map((c, i) => c + ((b[i] ?? 0) - c) * amount));
};

/** WCAG relative luminance, the one contrast ratios are defined on. */
const relativeLuminance = (hex: string) => {
  const [r, g, b] = channels(hex).map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
};

export const contrast = (a: string, b: string) => {
  const [high, low] = [relativeLuminance(a), relativeLuminance(b)].toSorted((x, y) => y - x);
  return ((high ?? 0) + 0.05) / ((low ?? 0) + 0.05);
};

/** White or ink on a colour, whichever reads better: a pale gold button needs dark text. */
export const readableOn = (hex: string): string =>
  contrast(hex, "#ffffff") >= contrast(hex, "#1f2544") ? "#ffffff" : "#1f2544";

/** The colour moved towards `toward` in small steps until it reads on `against`. */
const deepen = (color: string, toward: string, against: string, target: number) => {
  for (let step = 0; step <= 20; step++) {
    const candidate = mix(color, toward, step / 20);
    if (contrast(candidate, against) >= target) return candidate;
  }
  return toward;
};

export const themeLabel = (theme: ListTheme) => THEME_PALETTES[theme]?.label ?? theme;

/**
 * Suggested button colours per palette: the palette's own accent first, then tones that
 * carry a white label on light palettes, or a dark one on the night palette.
 */
const LIGHT_ACCENTS = [
  "#cf5a41",
  "#b3322b",
  "#e8613a",
  "#c95c7c",
  "#7a5fb8",
  "#4f84b0",
  "#23857a",
  "#557f5b",
  "#9c7644",
  "#2b2552",
];
const DARK_ACCENTS = [
  "#e9b949",
  "#f2a07b",
  "#f28fb0",
  "#c3b1f7",
  "#8ec5f0",
  "#7fd6c2",
  "#a8d08d",
  "#f6f1e4",
];

export const accentSuggestions = (theme: ListTheme): string[] => {
  const palette = THEME_PALETTES[theme] ?? THEME_PALETTES.classic;
  const pool = palette.dark ? DARK_ACCENTS : LIGHT_ACCENTS;
  return [
    palette.accent,
    ...pool.filter((hex) => hex.toLowerCase() !== palette.accent.toLowerCase()),
  ].slice(0, 10);
};

/**
 * Display face per font pair. Body text is always Atkinson Hyperlegible: it was drawn for
 * low vision, and the public page is read by grandparents on a phone.
 */
export const FONT_DISPLAY_CLASS: Record<ListFont, string> = {
  ronde: "font-display",
  elegante: "font-elegant",
  manuscrite: "font-hand",
  editoriale: "font-editorial",
};

export const FONT_LABELS: Record<ListFont, { label: string; sample: string }> = {
  ronde: { label: "Ronde", sample: "Joyeux Noël" },
  elegante: { label: "Élégante", sample: "Notre mariage" },
  manuscrite: { label: "Manuscrite", sample: "Mes envies" },
  editoriale: { label: "Éditoriale", sample: "La sélection" },
};

export const displayFont = (appearance: ListAppearance) =>
  FONT_DISPLAY_CLASS[appearance.font ?? TEMPLATE_DEFAULT_FONT[appearance.template]];

export const packById = (id: string | null) => STICKER_PACKS.find((pack) => pack.id === id);

export const packStickerUrls = (packId: string) =>
  packById(packId)?.stickers.map((sticker) => stickerUrl(packId, sticker.id)) ?? [];

/**
 * The stickers a page decorates with: the chosen pack's, or none at all when the owner
 * picked "Sans stickers". The occasion's pack only stands in for lists saved before packs
 * existed (see normalizeAppearance).
 */
export const decorStickers = (appearance: ListAppearance, _occasion?: ListOccasion): string[] =>
  appearance.stickers.map((sticker) => sticker.url);

/** A stable sticker for a wish without a photo: the same wish always gets the same one. */
export const stickerFor = (stickers: string[], seed: string): string | null => {
  if (stickers.length === 0) return null;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return stickers[Math.abs(hash) % stickers.length] ?? null;
};

/**
 * The motif as a background. A border motif is a frieze along the top edge; the others
 * tile. Tiles are transparent, so the palette colour shows through.
 */
export const motifStyle = (
  motif: { layout: string; tileUrl: string } | null,
  size = 360,
): CSSProperties =>
  motif
    ? motif.layout === "bordure"
      ? {
          backgroundImage: `url(${motif.tileUrl})`,
          backgroundRepeat: "repeat-x",
          backgroundPosition: "top center",
          backgroundSize: `auto ${Math.round(size / 3)}px`,
        }
      : {
          backgroundImage: `url(${motif.tileUrl})`,
          backgroundRepeat: "repeat",
          backgroundSize: `${size}px`,
        }
    : {};

export const motifOf = (pack: string, layout: MotifLayoutId) => ({
  pack,
  layout,
  tileUrl: motifTileUrl(pack, layout),
});

/** The house gift sticker, for a product with no photo outside any list's context. */
export const FALLBACK_STICKER = stickerUrl("noel", "cadeau");

/**
 * The appearance with every field filled. An API older than the templates, or a list
 * saved before they existed, may omit some; the occasion's defaults stand in so the page
 * always renders a real template rather than a broken one.
 */
export const normalizeAppearance = (
  appearance: Partial<ListAppearance> | null | undefined,
  occasion: ListOccasion,
): ListAppearance => {
  // No template means the list predates templates (or the API does): give it the whole
  // occasion look, stickers included. With a template, a null pack is a real choice.
  if (!appearance?.template) {
    const pack = OCCASION_CONFIG[occasion].defaultStickerPack;
    return {
      template: OCCASION_CONFIG[occasion].defaultTemplate,
      font: appearance?.font ?? null,
      motif: appearance?.motif ?? null,
      stickerPack: pack,
      stickers: packStickerUrls(pack).map((url, index) => ({ id: String(index), url })),
    };
  }
  return {
    template: appearance.template,
    font: appearance.font ?? null,
    motif: appearance.motif ?? null,
    stickerPack: appearance.stickerPack ?? null,
    stickers: appearance.stickers ?? [],
  };
};
